import React from 'react';

import {
  ErrorView as Error,
  NoPermissions,
  NotAuthorizedView,
  NotFound,
  UserPermissions,
  rolesChildren,
} from '@gasbi/shared';
import {
  SubdivisionDetailsPage,
  SubdivisionsPage,
} from '@gasbi/shared/web/Organization';
import { OrganizationPages } from '@gasbi/shared/web/Organization/pages';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';

import { Pages } from '@/pages';
import { makeSSOIntegrationsRoute } from '@/pages/SSOIntegrations/util';
import { BmsEmployeeRedirect } from '@/pages/ServiceEmployeeDetails/BmsEmployeeRedirect';
import { allUsersDetailsRoutes } from '@/pages/UsersDetails/routes';
import { workingPeriodsRoutes } from '@/pages/WorkingPeriods/routes';
import {
  AuthGuard,
  AuthInitProvider,
  OrganizationApiContextWrapper,
} from '@/providers';
import { OnboardingGate } from '@/providers/OnboardingGate';

const AuthCallback = React.lazy(() => import('@/pages/auth/AuthCallback'));
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));
const MyOrganization = React.lazy(() => import('@/pages/MyOrganization'));
const AdminOrganization = React.lazy(() => import('@/pages/AdminOrganization'));
const SubdivisionInformation = React.lazy(
  () => import('@/pages/SubdivisionDetails/views/Information'),
);
const MyOrganizationInformation = React.lazy(
  () => import('@/pages/MyOrganization/views/Information'),
);
const MyOrganizationEmployeeGroups = React.lazy(
  () => import('@/pages/MyOrganization/views/EmployeeGroups'),
);
const MyOrganizationEmployees = React.lazy(
  () => import('@/pages/MyOrganization/views/Employees'),
);
const EmployeeDetails = React.lazy(() => import('@/pages/EmployeeDetails'));
const ServiceEmployeeDetails = React.lazy(
  () => import('@/pages/ServiceEmployeeDetails'),
);
const Onboarding = React.lazy(() => import('@/pages/Onboarding'));
const MyOrganizations = React.lazy(
  () => import('@/pages/MyOrganizations/MyOrganizationsRoute'),
);
const MyOrganizationsList = React.lazy(() => import('@/pages/MyOrganizations'));
const ReferenceBooks = React.lazy(() => import('@/pages/ReferenceBooks'));
const ReferenceBook = React.lazy(() => import('@/pages/ReferenceBook'));
const ReferenceBookMeteringDeviceTypes = React.lazy(
  () => import('@/pages/ReferenceBookMeteringDeviceTypes'),
);
const ReferenceBookMeteringDeviceType = React.lazy(
  () => import('@/pages/ReferenceBookMeteringDeviceType'),
);

const Users = React.lazy(() => import('@/pages/Users'));
const UsersDetails = React.lazy(() => import('@/pages/UsersDetails'));
const SetupApiKeys = React.lazy(() => import('@/pages/SetupApiKeys'));
const ApiKeys = React.lazy(() => import('@/pages/ApiKeys'));
const DistributionTariffs = React.lazy(
  () => import('@/pages/DistributionTariffs'),
);
const DistributionTariffDetails = React.lazy(
  () => import('@/pages/DistributionTariffDetails'),
);
const WorkingPeriods = React.lazy(() => import('@/pages/WorkingPeriods'));
const WorkingPeriodDetails = React.lazy(
  () => import('@/pages/WorkingPeriodDetails'),
);
const WorkingPeriodForMainOrganizationDetails = React.lazy(
  () => import('@/pages/WorkingPeriodForMainOrganizationDetails'),
);
const SubdivisionEmployees = React.lazy(
  () => import('@/pages/SubdivisionEmployees'),
);
const SubsystemDetails = React.lazy(() => import('@/pages/SubsystemDetails'));

const rolesRoute = (isMainOrganizationView: boolean) => ({
  element: (
    <OrganizationApiContextWrapper
      isMainOrganizationView={isMainOrganizationView}
    >
      <Outlet />
    </OrganizationApiContextWrapper>
  ),
  children: rolesChildren(isMainOrganizationView),
});

// A структурний підрозділ's card: its information, edited by section from «Дії» (GSB-8433), with its representative and
// signatory (GSB-8522), and, since GSB-8373, the філія employees holding roles on it.
const subdivisionDetailsChildren = [
  { index: true, element: <SubdivisionInformation /> },
  { path: Pages.EMPLOYEES_PART, element: <SubdivisionEmployees /> },
];

const protectedRoutes = [
  {
    path: Pages.ROOT,
    element: <Navigate to={Pages.AUTH_CALLBACK} replace />,
  },
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthCallback />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedView />,
  },
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    path: Pages.SETUP_API_KEYS,
    element: <SetupApiKeys />,
  },
  {
    element: <MyOrganizations />,
    children: [
      {
        path: Pages.ORGANIZATIONS,
        // The ГРМ card (created here, PO, 2026-10-27), its підрозділи and roles are the shared legacy pages.
        element: (
          <OrganizationApiContextWrapper>
            <Outlet />
          </OrganizationApiContextWrapper>
        ),
        children: [
          { index: true, element: <MyOrganizationsList /> },
          {
            path: ':orgId',
            element: <MyOrganization />,
            children: [
              { index: true, element: <MyOrganizationInformation /> },
              { path: Pages.SUBDIVISIONS_PART, element: <SubdivisionsPage /> },
              {
                path: Pages.EMPLOYEES_PART,
                element: <MyOrganizationEmployeeGroups />,
              },
            ],
          },
          {
            path: `:orgId/${Pages.SUBDIVISIONS_PART}/:subId`,
            element: <SubdivisionDetailsPage />,
            children: subdivisionDetailsChildren,
          },
          {
            path: `:orgId/${Pages.EMPLOYEES_PART}/user/:identityId`,
            element: <EmployeeDetails />,
          },
          // An employee as the owning service keeps it (GSB-8222), opened from the employees tab.
          {
            path: `:orgId/${Pages.EMPLOYEES_PART}/services/:serviceId/:employeeId`,
            element: <ServiceEmployeeDetails />,
          },
          // A ГРМ's representative and a підрозділ's representative and signatory, linked from their blocks (GSB-8522).
          {
            path: `:orgId/${Pages.EMPLOYEES_PART}/:employeeId`,
            element: <BmsEmployeeRedirect />,
          },
          {
            path: `:orgId/${Pages.SUBDIVISIONS_PART}/:subId/${Pages.EMPLOYEES_PART}/:employeeId`,
            element: <BmsEmployeeRedirect />,
          },
          {
            path: `:orgId/${OrganizationPages.ROLES_PART}`,
            children: rolesChildren(false),
          },
        ],
      },
      {
        path: Pages.ADMIN_SERVICE,
        element: <AdminOrganization />,
        children: [
          {
            index: true,
            element: <Navigate to={Pages.EMPLOYEES_PART} replace />,
          },
          {
            path: Pages.EMPLOYEES_PART,
            element: <MyOrganizationEmployees />,
          },
        ],
      },
      {
        path: `${Pages.ADMIN_SERVICE}/${Pages.EMPLOYEES_PART}/:employeeId`,
        element: <EmployeeDetails />,
      },
      {
        path: `${Pages.ADMIN_SERVICE}/${OrganizationPages.ROLES_PART}`,
        ...rolesRoute(true),
      },
      { path: Pages.REFERENCE_BOOKS, element: <ReferenceBooks /> },
      {
        path: Pages.USERS,
        element: <Users />,
        handle: {
          keepAlive: true,
          permissions: UserPermissions.AdministrationUserRead,
        },
      },
      {
        path: `${Pages.USERS}/:identityId`,
        element: <UsersDetails />,
        handle: {
          permissions: UserPermissions.AdministrationUserRead,
        },
        children: allUsersDetailsRoutes,
      },
      {
        path: `${Pages.REFERENCE_BOOKS}/${Pages.METERING_DEVICE_TYPES}`,
        element: <ReferenceBookMeteringDeviceTypes />,
      },
      {
        path: `${Pages.REFERENCE_BOOKS}/${Pages.METERING_DEVICE_TYPES}/:meteringDeviceTypeGroupId`,
        element: <ReferenceBookMeteringDeviceType />,
      },
      { path: `${Pages.REFERENCE_BOOKS}/:type`, element: <ReferenceBook /> },
      { path: Pages.API_KEYS, element: <ApiKeys /> },
      makeSSOIntegrationsRoute(),
      // «Тарифи з розподілу», «Робочі періоди» and «Підсистеми», moved from BMS with its paths (PO, 2026-10-27)
      {
        path: Pages.DISTRIBUTION_TARIFF,
        element: <DistributionTariffs />,
        handle: {
          permissions: UserPermissions.AdministrationDistributionTariffRead,
        },
      },
      {
        path: `${Pages.DISTRIBUTION_TARIFF}/:organizationId`,
        element: <DistributionTariffDetails />,
        handle: {
          permissions: UserPermissions.AdministrationDistributionTariffRead,
        },
      },
      {
        path: Pages.WORKING_PERIODS,
        element: <WorkingPeriods />,
        children: workingPeriodsRoutes,
        handle: {
          permissions: UserPermissions.AdministrationWorkingPeriodRead,
        },
      },
      {
        path: `${Pages.WORKING_PERIODS}/:workingPeriodId`,
        element: <WorkingPeriodDetails />,
        handle: {
          permissions: UserPermissions.AdministrationWorkingPeriodRead,
        },
      },
      {
        path: `${Pages.WORKING_PERIODS_MAIN_ORGANIZATION}/:workingPeriodId`,
        element: <WorkingPeriodForMainOrganizationDetails />,
        handle: {
          permissions: UserPermissions.AdministrationWorkingPeriodRead,
        },
      },
      {
        path: `${Pages.WORKING_PERIODS}/${Pages.SUBSYSTEMS}/:subsystemId`,
        element: <SubsystemDetails />,
        handle: {
          permissions: UserPermissions.AdministrationWorkingPeriodRead,
        },
      },
    ],
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    element: <OnboardingGate />,
    children: [
      {
        path: Pages.ONBOARDING,
        element: <Onboarding />,
      },
      {
        element: (
          <AuthInitProvider>
            <AuthGuard />
          </AuthInitProvider>
        ),
        children: protectedRoutes,
      },
    ],
  },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
